/**
 * Purchasing Tabs Settings — toggle sub-tabs in the Purchasing page.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { ShoppingCart } from 'lucide-react';
import type { PurchasingTabsVisibility } from '../../../lib/api/types/common';
import { ACTIVE_PURCHASING_TAB_REGISTRY } from './purchasingTabsConfig';

interface PurchasingTabsTabProps {
  purchasingTabs: PurchasingTabsVisibility;
  saving: boolean;
  onPurchasingTabsChange: (tabs: PurchasingTabsVisibility) => void;
  onSave: () => void;
}

export function PurchasingTabsTab({ purchasingTabs, saving, onPurchasingTabsChange, onSave }: PurchasingTabsTabProps) {
  const handleToggle = (tabId: string, enabled: boolean) => {
    onPurchasingTabsChange({ ...purchasingTabs, [tabId]: enabled });
  };

  const enabledCount = ACTIVE_PURCHASING_TAB_REGISTRY.filter(tab => purchasingTabs[tab.id] ?? true).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Sales Orders Tab Visibility
          </CardTitle>
          <CardDescription>
            Choose which tabs are visible on the Sales Orders page. Disabled tabs will be hidden.
          </CardDescription>
          <span className="text-sm text-muted-foreground pt-2">
            {enabledCount} of {ACTIVE_PURCHASING_TAB_REGISTRY.length} tabs enabled
          </span>
        </CardHeader>
        <CardContent className="space-y-3">
          {ACTIVE_PURCHASING_TAB_REGISTRY.map((tab) => (
            <div key={tab.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
              <div>
                <Label className="text-base font-medium">{tab.label}</Label>
                <p className="text-sm text-muted-foreground">{tab.description}</p>
              </div>
              <Switch
                checked={purchasingTabs[tab.id] ?? true}
                onCheckedChange={(checked) => handleToggle(tab.id, checked)}
              />
            </div>
          ))}
          <Button onClick={onSave} disabled={saving} className="w-full mt-4">
            {saving ? 'Saving...' : 'Save Sales Orders Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
